import type { StateCreator } from 'zustand/vanilla';

export type ModalState<Props = unknown> = {
	open: boolean;
	props?: Props;
};

export type ModalLifecycle<Props = unknown> = {
	onOpen?: (props?: Props) => void;
	onClose?: (props?: Props) => void;
};

export type GlobalStore = {
	activeModals: Record<string, ModalState>;

	openModalHandler: <P = unknown>(name: string, props?: P) => void;
	closeModalHandler: (name: string) => void;
	registerModalLifecycle: <P = unknown>(name: string, lifecycle: ModalLifecycle<P>) => void;
	unregisterModalLifecycle: (name: string) => void;
	isOpenModal: (name: string) => boolean;
	getModalProps: <P = unknown>(name: string) => P | undefined; // helper type-safe
};

const modalLifecycles = new Map<string, ModalLifecycle<unknown>>();

export const createGlobalSlice: StateCreator<GlobalStore> = (set, get) => ({
	activeModals: {},

	openModalHandler: (name, props) => {
		const lifecycle = modalLifecycles.get(name);
		lifecycle?.onOpen?.(props);

		set((state) => ({
			activeModals: {
				...state.activeModals,
				[name]: { open: true, props },
			},
		}));
	},

	closeModalHandler: (name) => {
		const current = get().activeModals[name];
		if (!current) return;

		const lifecycle = modalLifecycles.get(name);
		lifecycle?.onClose?.(current.props);

		set((state) => {
			const newActiveModals = { ...state.activeModals };
			delete newActiveModals[name];
			return { activeModals: newActiveModals };
		});
	},

	registerModalLifecycle: (name, lifecycle) => {
		modalLifecycles.set(name, lifecycle as ModalLifecycle<unknown>);
	},

	unregisterModalLifecycle: (name) => {
		modalLifecycles.delete(name);
	},

	isOpenModal: (name) => !!get().activeModals[name],

	getModalProps: <P = unknown>(name: string): P | undefined => get().activeModals[name]?.props as P | undefined,
});
